/**
 * LayoutTemplateLoader - автоматичне завантаження шаблонів layouts
 *
 * Сканує директорію Ship/Templates/Layouts та автоматично реєструє всі шаблони,
 * які закінчуються на *LayoutTemplate
 *
 * IMPROVEMENTS:
 * - Lazy loading для шаблонів
 * - Валідація компонентів
 * - Template registry з metadata
 * - Error handling та logging
 */

import type { ILayoutTemplateConfig } from '../../Contracts/Configs'
import type { ComponentType } from 'react'

/**
 * Metadata для layout шаблону
 */
interface LayoutTemplateMetadata {
    name: string
    path: string
    component: ComponentType<any>
    accessFn?: (user: any) => boolean
    templateError403?: ComponentType<any>
    loadData?: () => Promise<any>
    loadTemplate?: ComponentType<any>
    loadedAt: number
    isLazy: boolean
}

/**
 * Template Registry для управління шаблонами
 */
class TemplateRegistry {
    private templates: Map<string, LayoutTemplateMetadata> = new Map()
    private lazyLoaders: Map<string, () => Promise<any>> = new Map()

    register(name: string, metadata: LayoutTemplateMetadata): void {
        this.templates.set(name, metadata)
    }

    registerLazy(name: string, loader: () => Promise<any>): void {
        this.lazyLoaders.set(name, loader)
    }

    async load(name: string): Promise<ComponentType<any> | undefined> {
        // Якщо вже завантажено
        if (this.templates.has(name)) {
            return this.templates.get(name)!.component
        }

        // Lazy loading
        if (this.lazyLoaders.has(name)) {
            const loader = this.lazyLoaders.get(name)!
            const module = await loader()
            const component = this.extractComponent(module, name)
            
            if (component) {
                this.register(name, {
                    name,
                    path: `lazy:${name}`,
                    component,
                    accessFn: module.accessFn,
                    templateError403: module.templateError403,
                    loadData: module.loadData,
                    loadTemplate: module.loadTemplate,
                    loadedAt: Date.now(),
                    isLazy: true,
                })
                return component
            }
        }

        console.warn(`[TemplateRegistry] Template not found: ${name}`)
        return undefined
    }

    has(name: string): boolean {
        return this.templates.has(name) || this.lazyLoaders.has(name)
    }

    get(name: string): LayoutTemplateMetadata | undefined {
        return this.templates.get(name)
    }

    getAll(): LayoutTemplateMetadata[] {
        return Array.from(this.templates.values())
    }

    getStats() {
        return {
            total: this.templates.size + this.lazyLoaders.size,
            loaded: this.templates.size,
            lazy: this.lazyLoaders.size,
            templates: Array.from(this.templates.keys()),
            lazyTemplates: Array.from(this.lazyLoaders.keys()),
        }
    }

    private extractComponent(module: any, name: string): ComponentType<any> | undefined {
        const componentName = `${this.capitalizeFirst(name)}LayoutTemplate`
        return module[componentName] || module.default
    }

    private capitalizeFirst(str: string): string {
        return str.charAt(0).toUpperCase() + str.slice(1)
    }
}

export const templateRegistry = new TemplateRegistry()

/**
 * Валідація React компонента
 */
function validateComponent(component: any, name: string): boolean {
    if (!component) {
        console.error(`[LayoutTemplateLoader] Component is undefined: ${name}`)
        return false
    }

    if (typeof component !== 'function' && typeof component !== 'object') {
        console.error(`[LayoutTemplateLoader] Invalid component type: ${name}`)
        return false
    }

    return true
}

/**
 * Eager loading - автоматично імпортує всі шаблони з директорії Ship/Templates/Layouts
 * Підтримує як .tsx, так і .jsx файли
 *
 * ВАЖЛИВО: import.meta.glob має викликатися безпосередньо, без присвоєння import.meta в змінну
 */
const templateModules = import.meta.glob('/src/Ship/Templates/Layouts/*LayoutTemplate.{tsx,jsx}', {
    eager: true,
}) as Record<string, Record<string, any>>

/**
 * Lazy loading modules
 */
const lazyTemplateModules = import.meta.glob('/src/Ship/Templates/Layouts/*LayoutTemplate.{tsx,jsx}', {
    eager: false,
}) as Record<string, () => Promise<Record<string, any>>>

/**
 * Створює конфігурацію шаблонів на основі файлів
 */
export function createLayoutTemplateConfig(): ILayoutTemplateConfig {
    const config: ILayoutTemplateConfig = {}

    // Register lazy loaders
    for (const [path, loader] of Object.entries(lazyTemplateModules)) {
        const fileName = path.split('/').pop()?.replace(/\.(tsx|jsx)$/, '') || ''
        const templateName = fileName.replace('LayoutTemplate', '').toLowerCase()

        if (templateName) {
            templateRegistry.registerLazy(templateName, loader)
        }
    }

    // Process eager loaded templates
    for (const [path, module] of Object.entries(templateModules)) {
        // Витягуємо назву файлу без шляху та розширення
        // Приклад: /app/src/Ship/Templates/Layouts/DefaultLayoutTemplate.tsx -> DefaultLayoutTemplate
        // Підтримуємо як .tsx, так і .jsx розширення
        const fileName =
            path
                .split('/')
                .pop()
                ?.replace(/\.(tsx|jsx)$/, '') || ''

        // Витягуємо назву шаблону (все що перед LayoutTemplate)
        // DefaultLayoutTemplate -> default
        // PublicLayoutTemplate -> public
        const templateName = fileName.replace('LayoutTemplate', '').toLowerCase()

        // Шукаємо експортований компонент (має бути з назвою файлу)
        const Component = module[fileName]

        if (!Component) {
            console.warn(`[LayoutTemplateLoader] Component not found in module: ${fileName}`)
            continue
        }

        // Validate component
        if (!validateComponent(Component, templateName)) {
            console.warn(`[LayoutTemplateLoader] Skipping invalid template: ${templateName}`)
            continue
        }

        // Register in template registry
        templateRegistry.register(templateName, {
            name: templateName,
            path,
            component: Component,
            accessFn: (module as any).accessFn,
            templateError403: (module as any).templateError403,
            loadData: (module as any).loadData,
            loadTemplate: (module as any).loadTemplate,
            loadedAt: Date.now(),
            isLazy: false,
        })

        // Зберігаємо компонент в конфігурації
        config[templateName] = Component

        // Прикріплюємо accessFn, templateError403, loadData та loadTemplate до компонента, якщо вони є в модулі
        const moduleTyped = module as any
        if (moduleTyped.accessFn) {
            ;(Component as any).accessFn = moduleTyped.accessFn
        }

        if (moduleTyped.templateError403) {
            ;(Component as any).templateError403 = moduleTyped.templateError403
        }

        if (moduleTyped.loadData) {
            ;(Component as any).loadData = moduleTyped.loadData
        }

        if (moduleTyped.loadTemplate) {
            ;(Component as any).loadTemplate = moduleTyped.loadTemplate
        }
    }

    return config
}

/**
 * Глобальна конфігурація шаблонів
 */
export const LayoutTemplateConfig = createLayoutTemplateConfig()
